/**
 * THE ONE WAY A PHOTO IS TAKEN — extracted from `PhotoCapture` on 2026-08-30
 * (D-028) so the photo shelf and the per-question drawer share it instead of
 * one of them growing a second copy.
 *
 * Everything load-bearing about the capture path lives here, verbatim from
 * where it was written and why:
 *
 *   - `resolvePhotoType` at capture, so a refusal reaches the surveyor while
 *     they are holding the phone in front of the subject, not hours later
 *     from a queue that will not drain (D-022, "validate at capture");
 *   - the bytes are read NOW, into memory, and what is stored is a Blob built
 *     from a COPY of them — never a slice or a view of the camera's File. A
 *     File the phone cannot read comes back empty here and is refused in one
 *     sentence; the alternative uploaded zero bytes on every drain for twelve
 *     days (2026-08-19, src/db/photo-file.ts module header);
 *   - the `PhotoRef` literal is built in exactly ONE place — this file — and
 *     handed to `store.addPhoto`, which commits the ref, the blob and the
 *     outbox row in one transaction. Nothing else in the app constructs one;
 *   - `input.value` is cleared before anything else, because iOS will not
 *     fire `change` for a re-shot file of the same name otherwise;
 *   - ONE hidden input, no `capture` attribute (D-030 R10): omitting it is
 *     what makes the OS show its own sheet — Take Photo / Photo Library /
 *     Choose Files — instead of the app drawing that choice itself one tap
 *     earlier (D-015: both sources, both real, and the OS's own sheet
 *     already offers both).
 *
 * A consumer renders the one input from `inputProps`, opens it with `open()`
 * from its own button, and prints `error` ABOVE that button (D-011 item 6).
 * `remove` and `retry` are here too so a tile, a viewer and a drawer all
 * delete and re-send through the same lines.
 *
 * ── Retake, without losing the gesture (round 7 task 2) ─────────────────────
 * iOS Safari only honours a file input's programmatic `.click()` when it runs
 * SYNCHRONOUSLY inside the tap that triggered it; an `await` first and the
 * activation is spent, and the picker never opens. So `open(options)` clicks
 * the input immediately and remembers `options.replaces` in a ref — nothing
 * is deleted yet.
 *
 * `capture` reads and clears that ref in its FIRST line, synchronously,
 * before any `await` — not after `resolvePhotoType` / `readPhotoBytes` /
 * `addPhoto` have all resolved. Reading it late (fix round 1, 2026-09-03)
 * let a second `open()` racing that window — another Retake, or the
 * `cancel` listener below — steal or erase a promise that belonged to THIS
 * `change`, not the next one. Binding the local to the event that consumed
 * it is what makes the delete happen for the batch that actually landed,
 * and never at all if the OS sheet is cancelled before any file does.
 *
 * ── The capture-in-flight flag (final review's fix wave) ────────────────────
 * `open()` also marks a capture in flight (`src/ui/captureInFlight.ts`), and
 * `change` and `cancel` both clear it. The reader is the app-update gate:
 * on iOS the camera and the picker background the page, which used to be the
 * signal for a deferred build to reload — under the picker, losing the
 * picture and the `replacingRef` promise above with it. While the flag is
 * set `shouldReloadNow` answers false and the build waits for the next
 * screen change or background instead.
 */

import { useRef, useState, type ChangeEvent, type InputHTMLAttributes, type RefCallback } from 'react';

import { photoR2Key, type PhotoRef } from '../../core/types';
import { photoBlobFromBytes, readPhotoBytes, resolvePhotoType } from '../../db/photo-file';
import { isoAt } from '../../db/store';
import { clearCaptureInFlight, markCaptureInFlight } from '../captureInFlight';
import { makeThumbnail } from '../state/makeThumbnail';
import { randomId } from '../state/newSurvey';
import { useServices } from '../state/services';
import { useSurvey } from '../state/SurveyProvider';
import { guardedWrite } from '../state/writeGate';

export interface PhotoScope {
  sectionInstanceId: string;
  /** Absent = a section-level photo. */
  questionId?: string;
  /** Set when this capture satisfies a recommended shot from the shot list. */
  shotListId?: string;
}

export interface PhotoCaptureApi {
  /**
   * Spread onto ONE hidden `<input type="file">` — D-030 R10. No `capture`
   * attribute, deliberately: without it the OS opens its own sheet (Take
   * Photo / Photo Library / Choose Files), which is the two-option menu the
   * app used to draw itself, one tap earlier and maintained by Apple.
   */
  inputProps: InputHTMLAttributes<HTMLInputElement> & { ref: RefCallback<HTMLInputElement> };
  /**
   * Click the input — the native sheet does the rest. Pass `replaces` for a
   * Retake: that photo id is deleted once (and only once) the first new file
   * lands; cancelling the sheet leaves it untouched.
   */
  open: (options?: { replaces?: string }) => void;
  /** True while a picked file is being read and written. */
  busy: boolean;
  /** Refusal sentences, or a write failure — printed above the buttons. */
  error: string | null;
  clearError: () => void;
  /** `store.deletePhoto` — one transaction, never touches R2. */
  remove: (photoId: string) => Promise<void>;
  /** `sync.retryBlockedPhoto` — one request, not a loop. */
  retry: (photoId: string) => Promise<void>;
}

export function usePhotoCapture(scope: PhotoScope): PhotoCaptureApi {
  const { store, sync, log } = useServices();
  const { surveyId, readOnly, doc } = useSurvey();
  const inputRef = useRef<HTMLInputElement>(null);
  /** Set by `open({ replaces })`; the photo id a landed capture will delete. */
  const replacingRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { sectionInstanceId, questionId, shotListId } = scope;

  /*
   * Browsers fire `cancel` on a file input when its picker is dismissed with
   * nothing chosen (Chromium, Safari 16.4+). That is the one place a Retake
   * can end without a new photo ever reaching `capture` — so it is where the
   * promise to delete the old one is called off.
   *
   * Final review, important 1 (round 7): a `[]`-deps effect bound this
   * against `inputRef.current` at the hook's FIRST render — fine for a
   * consumer that always renders the input, but `PhotoShelf` hides it when
   * `readOnly && cards.length === 0` (PhotoShelf.tsx). On that path
   * `inputRef.current` is null when the effect runs and it binds nothing;
   * office recall later flips `readOnly` false in place (no remount), the
   * input mounts for the first time — and this hook never runs again to
   * bind it. A callback ref does not have that gap: React calls it, with
   * the real node, every time the node the `ref` attribute is on mounts —
   * including a mount long after this hook's first render — and (React 19)
   * runs whatever it returns when that same node unmounts, so add/remove
   * stays paired without a second effect.
   */
  const bindInput: RefCallback<HTMLInputElement> = (node) => {
    inputRef.current = node;
    if (!node) return;
    const onCancel = (): void => {
      replacingRef.current = null;
      // The sheet is down with nothing chosen: a build may reload again.
      clearCaptureInFlight();
    };
    node.addEventListener('cancel', onCancel);
    return () => node.removeEventListener('cancel', onCancel);
  };

  const capture = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    // Read AND clear the replacement promise here, synchronously, in the same
    // task as the `change` event — not after the awaits below. `open()` can
    // be called again (another Retake) while this batch is still reading
    // bytes or writing to IndexedDB, and reading `replacingRef.current` late
    // would bind THIS capture to whatever the NEXT `open()` happened to set,
    // or to nothing if the `cancel` listener happened to clear it first.
    let pendingReplace = replacingRef.current;
    replacingRef.current = null;
    // The sheet has answered with files (or none): the picker is down, so the
    // app-update gate may consider a reload again. Cleared here, in the same
    // synchronous task as the promise above, for the same reason.
    clearCaptureInFlight();
    /*
     * Minor 6 (round 7 final review, controller ruling): "A Retake keeps the
     * photo's label — the picture is replaced, not its name." Read the
     * replaced photo's label NOW, from the live document, before anything is
     * deleted — `pendingReplace`'s id is gone from `doc.photos` the instant
     * `store.deletePhoto` below lands, so this is the only moment it can
     * still be read.
     */
    const replacedLabel = pendingReplace
      ? doc.photos.find((p) => p.id === pendingReplace)?.label
      : undefined;
    // Clear it immediately: re-shooting the same file name must fire `change`
    // again, and iOS will not if the value is still set.
    event.target.value = '';
    if (files.length === 0) return;

    /*
     * Round 7 task 10, ruling 12: `store.addPhoto` and `store.deletePhoto`
     * bypass the provider's `write()` — this hook is the one door they go
     * through, so the read-only check has to live here too, not only on the
     * buttons that normally reach it. Every visible way to trigger a
     * `change` on the hidden input is already gone (`PhotoShelf`'s add pill
     * and todo cards, `PhotoCapture`'s add button); this is the second line,
     * the same `guardedWrite` the provider's `write()` uses.
     */
    await guardedWrite({ readOnly, surveyId, door: 'photo.capture', log }, () =>
      captureFiles(files, pendingReplace, replacedLabel),
    );
  };

  const captureFiles = async (
    files: File[],
    pendingReplaceIn: string | null,
    replacedLabel: string | undefined,
  ): Promise<void> => {
    let pendingReplace = pendingReplaceIn;
    setBusy(true);
    // Refusals are collected rather than thrown: picking six photos out of the
    // library and having one of them be a screenshot the office cannot open
    // must still save the other five.
    const refused: string[] = [];
    try {
      for (const file of files) {
        /*
         * THE GUARD, AT THE ONLY MOMENT IT IS ANY USE.
         *
         * `resolvePhotoType` reads at most sixteen bytes and answers one of two
         * things: the type the office will accept this as, or the sentence
         * explaining why it will not. Either way the surveyor learns it NOW,
         * holding the phone, in front of the subject — not at a drain, hours
         * later, from a queue that will not empty.
         */
        const decided = await resolvePhotoType(file);
        if (!decided.ok) {
          refused.push(decided.message);
          continue;
        }

        /*
         * READ THE PICTURE NOW, INTO MEMORY (2026-08-19).
         *
         * What goes into IndexedDB is a Blob built from these bytes — a copy —
         * and never a slice or a view of the camera's File. A File the phone
         * cannot actually read comes back empty HERE, while the surveyor is
         * still standing in front of the subject, and is refused in one
         * sentence; the alternative was storing a reference that uploaded as
         * zero bytes on every drain for the rest of the survey (src/db/
         * photo-file.ts, module header).
         */
        const data = await readPhotoBytes(file);
        if (data.byteLength === 0) {
          refused.push(
            'The phone could not read that picture — it came through empty, so nothing was saved. Take it again.',
          );
          continue;
        }

        const id = `ph-${randomId()}`;
        const photo: PhotoRef = {
          id,
          surveyId,
          sectionInstanceId,
          ...(questionId ? { questionId } : {}),
          ...(shotListId ? { shotListId } : {}),
          // Minor 6: a Retake replaces the picture, not its name. Only the
          // FIRST new file of the batch can carry it — `pendingReplace` is
          // still set (not yet cleared by the delete below) for exactly that
          // one file, the same window that decides which file the old photo
          // is deleted for.
          ...(pendingReplace !== null && replacedLabel ? { label: replacedLabel } : {}),
          r2Key: photoR2Key(surveyId, sectionInstanceId, id, decided.extension, questionId),
          contentType: decided.contentType,
          // The bytes actually read, not `file.size`: the two are equal on a
          // healthy capture and the read is the one that is true.
          bytes: data.byteLength,
          capturedAt: isoAt(store.now()),
          // Nothing has left this phone yet, and that is the honest state to
          // store even when there is signal — the sync engine owns the change.
          uploadState: 'pending',
          attempts: 0,
        };
        // The bytes go in carrying the resolved type, so the Blob that comes
        // back out of IndexedDB at drain time still knows what it is — and they
        // go in as a COPY of the picture, not a view of the camera's file.
        const picture = photoBlobFromBytes(data, decided.contentType);
        // The thumbnail, made NOW, from the bytes just read (round 8.1, task
        // 3): 640 px on the longest side, so the shelf never draws a 4 MB
        // original on a 168-px card again. Null when this browser cannot
        // decode the picture — a HEIC, say — and null is not a refusal: the
        // photo is saved exactly as before, with no small copy beside it.
        const thumb = await makeThumbnail(picture);
        await store.addPhoto(surveyId, photo, picture, thumb);
        // Retake: the OLD photo is deleted only now — once a NEW one has
        // actually landed, the first file of the batch to make it in — never
        // before `open()` and never if every file in the batch is refused.
        if (pendingReplace !== null) {
          const staleId = pendingReplace;
          pendingReplace = null;
          // A failed delete must not read as a failed SAVE (the file above
          // landed fine) and must not abort the rest of the batch — reported
          // the same way `remove()` reports the same failure, and nothing
          // else.
          try {
            await store.deletePhoto(surveyId, staleId);
          } catch (cause) {
            refused.push(cause instanceof Error ? cause.message : 'Could not delete that photo.');
          }
        }
      }
      setError(refused.length > 0 ? refused.join(' ') : null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save that photo.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photoId: string): Promise<void> => {
    // Second line, same reasoning as `capture` above: every visible Delete is
    // already hidden (`PhotoCapture`, `PhotoViewer`, `PhotoShelf`'s viewer);
    // this is what stands if one of them is ever reached anyway.
    await guardedWrite({ readOnly, surveyId, door: 'photo.remove', log }, async () => {
      try {
        await store.deletePhoto(surveyId, photoId);
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not delete that photo.');
      }
    });
  };

  /**
   * Send a parked photo once more, because the surveyor said so. One request,
   * not a loop — see `SyncEngine.retryBlocked`. If it is refused again the row
   * simply parks again and the tile says the same thing; nothing is lost either
   * way, which is what the sentence above the buttons promises.
   */
  const retry = async (photoId: string): Promise<void> => {
    try {
      await sync.retryBlockedPhoto(photoId);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not try that photo again.');
    }
  };

  // ONE hidden input, one real button per place (D-030 R10). The button is
  // the accessible control; the input is only the picker it opens. No
  // `capture` attribute — that is what makes the OS offer camera AND library
  // in its own sheet instead of the app forcing one.
  return {
    inputProps: {
      className: 'sr-only',
      type: 'file',
      accept: 'image/*',
      multiple: true,
      tabIndex: -1,
      'aria-hidden': true,
      'data-source': 'photo',
      onChange: (event: ChangeEvent<HTMLInputElement>) => void capture(event),
      ref: bindInput,
    } as PhotoCaptureApi['inputProps'],
    open: (options?: { replaces?: string }) => {
      // Set BEFORE the click, and the click itself must stay synchronous —
      // no `await` anywhere above this line — or iOS Safari never opens the
      // sheet at all (module header, "Retake, without losing the gesture").
      replacingRef.current = options?.replaces ?? null;
      // Marked BEFORE the click: on iOS the page is hidden the instant the
      // sheet is up, and the gate reads this flag on that very event.
      markCaptureInFlight();
      inputRef.current?.click();
    },
    busy,
    error,
    clearError: () => setError(null),
    remove,
    retry,
  };
}

/** The state word a tile prints for an upload that is not parked. */
export const UPLOAD_LABEL: Record<PhotoRef['uploadState'], string> = {
  pending: 'On this phone',
  uploading: 'Sending…',
  uploaded: 'At the office',
  failed: 'Will retry',
};
